/* eslint-disable max-lines */
import type { ReactElement } from 'react';
import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';

import type { MaterialOnTheFlyValue } from './reducer';
import type {
    ExtendedMaterialOnTheFly,
    ExtendedMaterialSubcategory,
    ManufactureDetail,
    MaterialOnTheFly,
    MaterialSubcategory,
} from '@buying/library';

import { useQuery } from '@apollo/client/react';
import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { WarningIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import {
    GetMaterialOnTheFlyMandatoryOptionsDocument,
    GraphClientNames,
    isExtendedMaterialOnTheFly,
    isMaterialOnTheFly,
    RawMaterialsProvider,
} from '@buying/library';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ExtendedManufactureDetailsProvider from '../../providers/ExtendedManufactureDetailsProvider';
import ManufactureDetailsProvider from '../../providers/ManufactureDetailsProvider';

import { ManufactureDetailsSection } from './ManufactureDetailsSection';
import { MaterialOnTheFlyDialogTitle } from './MaterialOnTheFlyDialogTitle';
import { MaterialSubcategorySection } from './MaterialSubcategorySection';
import { RawMaterialsSection } from './RawMaterialsSection';
import {
    initialMaterialOnTheFlyValue,
    materialOnTheFlyReducer,
    MaterialOnTheFlyValueContext,
} from './reducer';
import { WeightSection } from './WeightSection';

let fadeOutDialogTimeout: NodeJS.Timeout | undefined = undefined;

interface MaterialOnTheFlyDialogProps {
    styleNumber: number;
    styleName?: string;
    open: boolean;
    usageName: string;
    usagePublicId?: string; // only for bom
    materialsOnTheFly: Array<ExtendedMaterialOnTheFly | MaterialOnTheFly>;
    isExtendedVersion?: boolean;
    materialSubcategories: Array<
        ExtendedMaterialSubcategory | MaterialSubcategory
    >;
    manufactureDetails?: Array<ManufactureDetail>;
    onClose: (newMaterialOnTheFly?: MaterialOnTheFlyValue) => void;
    onExtendedError?: (error: Error, code: string) => void;
    initialValue?: MaterialOnTheFlyValue;
    isNonApparel?: boolean;
}

const MaterialOnTheFlyDialog = ({
    styleNumber,
    styleName,
    open,
    usageName,
    usagePublicId,
    materialsOnTheFly,
    isExtendedVersion = false,
    manufactureDetails = [],
    materialSubcategories, // Send array with length 1 to preselect that one
    onClose,
    onExtendedError,
    initialValue,
    isNonApparel = false,
}: MaterialOnTheFlyDialogProps): ReactElement => {
    const { palette } = useTheme();

    // Material subcategory is only there for BOM, not for EC
    const includeMaterialSubcategory = !isExtendedVersion;
    // Weight is only there for BOM, not for EC
    const includeWeight = !isExtendedVersion;

    const [materialOnTheFlyValue, dispatchMaterialOnTheFly] = useReducer(
        materialOnTheFlyReducer,
        initialMaterialOnTheFlyValue,
        (initial) => {
            if (initialValue) {
                // Use initialValue.manufactureDetail, not materialOnTheFlyValue (which is undefined here)
                const transformedInitialValue: MaterialOnTheFlyValue = {
                    ...initial,
                    ...initialValue,
                    manufactureDetail: initialValue.manufactureDetail ?? null,
                    weightValue: includeWeight
                        ? (initialValue.weightValue ?? null)
                        : null,
                    weightUnit: includeWeight
                        ? (initialValue.weightUnit ?? null)
                        : null,
                    weightMeasure: includeWeight
                        ? (initialValue.weightMeasure ?? null)
                        : null,
                };
                return transformedInitialValue;
            }
            return {
                ...initial,
                materialSubcategory: null,
                manufactureDetail: null,
                compositions: [],
                weightValue: null,
                weightUnit: null,
                weightMeasure: null,
            };
        }
    );

    //we keep the latest compositions in a ref to be able to compare them in the useEffect
    // that checks if composition is optional and we need to clear the default 100% value on the initial row.
    const latestCompositionsRef = useRef(materialOnTheFlyValue.compositions);

    const { data } = useQuery(GetMaterialOnTheFlyMandatoryOptionsDocument, {
        fetchPolicy: 'network-only',
        context: { clientName: GraphClientNames['bestone-bi4-buying-bom-bff'] },
        skip:
            isExtendedVersion ||
            !materialOnTheFlyValue.materialSubcategory
                ?.materialSubcategoryPublicId,
        variables: {
            materialSubcategoryPublicId:
                materialOnTheFlyValue.materialSubcategory
                    ?.materialSubcategoryPublicId ?? '',
        },
    });

    // If we are on BOM, we can have three outcomes for weight:
    const isWeightRequired =
        data?.materialOnTheFlyOptions.isWeightMandatory === true;
    const isWeightOptional =
        data?.materialOnTheFlyOptions.isWeightMandatory === false;
    const isWeightForbidden =
        data?.materialOnTheFlyOptions.isWeightMandatory === null;

    // Composition is always required for extended composition.
    // For BOM, it follows the tri-state response from combination ruleset settings (mandatory, optional, forbidden (N/A)).
    const isCompositionRequired =
        isExtendedVersion ||
        data?.materialOnTheFlyOptions.isCompositionMandatory === true;
    const isCompositionOptional =
        !isExtendedVersion &&
        data?.materialOnTheFlyOptions.isCompositionMandatory === false;

    // when composition is forbidden (null), we hide the row material section + %
    const isCompositionForbidden =
        !isExtendedVersion &&
        data?.materialOnTheFlyOptions.isCompositionMandatory === null;

    // Manufacture detail is always required for extended composition.
    // For BOM, it follows the tri-state response from combination ruleset settings (mandatory, optional, forbidden (N/A)).
    const isManufactureDetailRequired =
        isExtendedVersion ||
        data?.materialOnTheFlyOptions.isManufactureDetailMandatory === true;
    const isManufactureDetailForbidden =
        !isExtendedVersion &&
        data?.materialOnTheFlyOptions.isManufactureDetailMandatory === null;

    // Show manufacture detail section for extended composition always, for BOM when not forbidden
    const includeManufactureDetail =
        isExtendedVersion || !isManufactureDetailForbidden;

    // Track whether the selected manufacture detail is still available in the options list
    const [isManufactureDetailValid, setIsManufactureDetailValid] =
        useState(true);

    const handleManufactureDetailValidityChange = useCallback(
        (isValid: boolean) => {
            setIsManufactureDetailValid(isValid);
        },
        []
    );

    // When editing, check if the user has made any changes compared to the initial values
    const hasChanges = useMemo((): boolean => {
        if (!initialValue) return true;

        if (
            materialOnTheFlyValue.materialSubcategory
                ?.materialSubcategoryPublicId !==
            initialValue.materialSubcategory?.materialSubcategoryPublicId
        ) {
            return true;
        }

        if (
            materialOnTheFlyValue.manufactureDetail
                ?.manufactureDetailPublicId !==
            initialValue.manufactureDetail?.manufactureDetailPublicId
        ) {
            return true;
        }

        if (
            materialOnTheFlyValue.weightValue !== initialValue.weightValue ||
            materialOnTheFlyValue.weightUnit?.unitOfMeasurementPublicId !==
                initialValue.weightUnit?.unitOfMeasurementPublicId ||
            materialOnTheFlyValue.weightMeasure?.unitOfMeasurementPublicId !==
                initialValue.weightMeasure?.unitOfMeasurementPublicId
        ) {
            return true;
        }

        const currentCompositions = materialOnTheFlyValue.compositions
            .filter((c) => !!c.rawMaterial?.rawMaterialPublicId)
            .map((c) => ({
                rawMaterialPublicId: c.rawMaterial?.rawMaterialPublicId ?? '',
                percentage: c.percentage ?? 0,
            }));

        const initialCompositions = initialValue.compositions
            .filter((c) => !!c.rawMaterial?.rawMaterialPublicId)
            .map((c) => ({
                rawMaterialPublicId: c.rawMaterial?.rawMaterialPublicId ?? '',
                percentage: c.percentage ?? 0,
            }));

        if (currentCompositions.length !== initialCompositions.length) {
            return true;
        }

        const areCompositionsEqual = currentCompositions.every((current) =>
            initialCompositions.some(
                (initial) =>
                    initial.rawMaterialPublicId ===
                        current.rawMaterialPublicId &&
                    initial.percentage === current.percentage
            )
        );

        return !areCompositionsEqual;
    }, [
        initialValue,
        materialOnTheFlyValue.materialSubcategory?.materialSubcategoryPublicId,
        materialOnTheFlyValue.manufactureDetail?.manufactureDetailPublicId,
        materialOnTheFlyValue.weightValue,
        materialOnTheFlyValue.weightUnit?.unitOfMeasurementPublicId,
        materialOnTheFlyValue.weightMeasure?.unitOfMeasurementPublicId,
        materialOnTheFlyValue.compositions,
    ]);

    // we check if the mof to edit is a duplicate of an existing one
    // by comparing the material subcategory and compositions (raw material + percentage)
    const isDuplicate = useMemo((): boolean => {
        if (!materialsOnTheFly.length) return false;

        return materialsOnTheFly.some((materialOnTheFly) => {
            // When editing, skip comparing against the MOF being edited
            if (
                materialOnTheFlyValue.materialOnTheFlyPublicId &&
                materialOnTheFly.materialOnTheFlyPublicId ===
                    materialOnTheFlyValue.materialOnTheFlyPublicId
            ) {
                return false;
            }

            // Identical MOFs are allowed when reused across different usages
            if (
                'usagePublicId' in materialOnTheFly &&
                materialOnTheFly.usagePublicId !== usagePublicId
            ) {
                return false;
            }

            if (
                materialOnTheFly.materialSubcategory
                    .materialSubcategoryPublicId !==
                materialOnTheFlyValue.materialSubcategory
                    ?.materialSubcategoryPublicId
            ) {
                return false;
            }

            //this is what we have already in the database
            // we do this in order to avoid inconsistent comparisons
            const existingCompositionsForComparison = isCompositionForbidden
                ? []
                : materialOnTheFly.compositions.flatMap((composition) => {
                      if (!composition) {
                          return [];
                      }

                      if (!composition.rawMaterial.rawMaterialPublicId) {
                          return [];
                      }

                      return [
                          {
                              rawMaterialPublicId:
                                  composition.rawMaterial.rawMaterialPublicId,
                              percentage: composition.percentage,
                          },
                      ];
                  });

            //this is what the user has entered in the mof form
            const userCompositionsForComparison = isCompositionForbidden
                ? []
                : materialOnTheFlyValue.compositions
                      .filter(
                          (composition) =>
                              !!composition.rawMaterial?.rawMaterialPublicId
                      )
                      .map((composition) => ({
                          rawMaterialPublicId:
                              composition.rawMaterial?.rawMaterialPublicId ??
                              '',
                          percentage: composition.percentage ?? 0,
                      }));

            if (
                existingCompositionsForComparison.length !==
                userCompositionsForComparison.length
            ) {
                return false;
            }

            // Calculate if the compositions are exactly the same
            const areCompositionsEqual =
                existingCompositionsForComparison.every((composition) =>
                    userCompositionsForComparison.some(
                        (valueComposition) =>
                            valueComposition.rawMaterialPublicId ===
                                composition.rawMaterialPublicId &&
                            valueComposition.percentage ===
                                composition.percentage
                    )
                );

            // If the actual compositions are not the same, it's not a duplicate
            if (!areCompositionsEqual) return false;

            if (isExtendedVersion) {
                // If we are on extended composition

                // If the manufacture detail is different, it's not a duplicate
                if (
                    isExtendedMaterialOnTheFly(materialOnTheFly) &&
                    materialOnTheFly.manufactureDetail
                        .manufactureDetailPublicId !==
                        materialOnTheFlyValue.manufactureDetail
                            ?.manufactureDetailPublicId
                ) {
                    return false;
                }
            } else {
                // If we are on BOM
                // This condition is mostly to ensure type safety to the materialOnTheFly
                if (isMaterialOnTheFly(materialOnTheFly)) {
                    // If the weight is different, it's not a duplicate
                    const hasWeight =
                        !!materialOnTheFly.weightValue ||
                        !!materialOnTheFly.weightUnit ||
                        !!materialOnTheFly.weightMeasure;
                    const hasFormWeight =
                        !!materialOnTheFlyValue.weightValue ||
                        !!materialOnTheFlyValue.weightUnit ||
                        !!materialOnTheFlyValue.weightMeasure;

                    // If one has weight and the other doesn't, it's not a duplicate
                    if (hasWeight !== hasFormWeight) return false;

                    // If both have weight but they are different, it's not a duplicate
                    if (
                        isMaterialOnTheFly(materialOnTheFly) &&
                        (materialOnTheFly.weightValue !==
                            materialOnTheFlyValue.weightValue ||
                            materialOnTheFly.weightUnit
                                ?.unitOfMeasurementPublicId !==
                                materialOnTheFlyValue.weightUnit
                                    ?.unitOfMeasurementPublicId ||
                            materialOnTheFly.weightMeasure
                                ?.unitOfMeasurementPublicId !==
                                materialOnTheFlyValue.weightMeasure
                                    ?.unitOfMeasurementPublicId)
                    ) {
                        return false;
                    }

                    // If the manufacture detail is different, it's not a duplicate
                    const existingManufactureDetailId = (
                        materialOnTheFly.manufactureDetail as
                            | { manufactureDetailPublicId: string }
                            | null
                            | undefined
                    )?.manufactureDetailPublicId;
                    const formManufactureDetailId =
                        materialOnTheFlyValue.manufactureDetail
                            ?.manufactureDetailPublicId;

                    if (
                        existingManufactureDetailId !== formManufactureDetailId
                    ) {
                        return false;
                    }
                }
            }

            return true;
        });
    }, [
        materialsOnTheFly,
        materialOnTheFlyValue.materialSubcategory?.materialSubcategoryPublicId,
        materialOnTheFlyValue.materialOnTheFlyPublicId,
        materialOnTheFlyValue.compositions,
        materialOnTheFlyValue.manufactureDetail?.manufactureDetailPublicId,
        materialOnTheFlyValue.weightValue,
        materialOnTheFlyValue.weightUnit,
        materialOnTheFlyValue.weightMeasure,
        isCompositionForbidden,
        isExtendedVersion,
        usagePublicId,
    ]);

    const { disableSaveButton, disableMessage } = useMemo(() => {
        if (!materialOnTheFlyValue.materialSubcategory)
            return {
                disableSaveButton: true,
                disableMessage: 'Fill out all required fields',
            };

        // A row is "touched" if user entered composition data.
        // Ignore the optional placeholder row (no raw material + default 100%)
        // that is injected during initialization for empty compositions.
        const hasAnyCompositionValue = materialOnTheFlyValue.compositions.some(
            (composition) => {
                const isOptionalPlaceholderRow =
                    isCompositionOptional &&
                    materialOnTheFlyValue.compositions.length === 1 &&
                    !composition.rawMaterial &&
                    composition.percentage === 100;

                if (isOptionalPlaceholderRow) {
                    return false;
                }

                return !!composition.rawMaterial || !!composition.percentage;
            }
        );

        //if any selected raw material was deleted after combination ruleset changes, we block save and show error message
        const hasUnavailableRawMaterial =
            materialOnTheFlyValue.compositions.some(
                (composition) => composition.rawMaterial?.isDeleted
            );

        if (hasUnavailableRawMaterial) {
            return {
                disableSaveButton: true,
                disableMessage: 'Raw material is not available',
            };
        }

        if (
            isCompositionRequired ||
            (isCompositionOptional && hasAnyCompositionValue)
        ) {
            // Check if compositions add up to 100%
            const totalPercentage = [
                ...materialOnTheFlyValue.compositions,
            ].reduce((acc, c) => acc + (c.percentage ?? 0), 0);
            if (totalPercentage !== 100)
                return {
                    disableSaveButton: true,
                    disableMessage: 'Total for composition must be 100%',
                };

            // check if compositions have a valid raw material and percentage
            const someInvalid = [...materialOnTheFlyValue.compositions].some(
                (c) => !c.rawMaterial || !c.percentage
            );
            if (someInvalid)
                return {
                    disableSaveButton: true,
                    disableMessage: 'Fill out all required fields',
                };
        }

        // Block save when manufacture detail is required but not selected
        if (
            isManufactureDetailRequired &&
            !materialOnTheFlyValue.manufactureDetail
        ) {
            return {
                disableSaveButton: true,
                disableMessage: 'Fill out all required fields',
            };
        }

        if (includeWeight) {
            if (isWeightRequired) {
                // Check if weightValue is valid
                // weight unit and measure are filled by default in when empty
                if (
                    materialOnTheFlyValue.weightValue === null ||
                    materialOnTheFlyValue.weightValue <= 0
                ) {
                    return {
                        disableSaveButton: true,
                        disableMessage: 'Fill out all required fields',
                    };
                }

                // Check if weight unit is valid
                if (!materialOnTheFlyValue.weightUnit) {
                    return {
                        disableSaveButton: true,
                        disableMessage: 'Fill out all required fields',
                    };
                }

                // Check if weight measure is valid
                if (!materialOnTheFlyValue.weightMeasure) {
                    return {
                        disableSaveButton: true,
                        disableMessage: 'Fill out all required fields',
                    };
                }
            } else if (isWeightOptional) {
                const isWeightEmpty =
                    !materialOnTheFlyValue.weightValue &&
                    !materialOnTheFlyValue.weightUnit &&
                    !materialOnTheFlyValue.weightMeasure;
                const isWeightComplete =
                    !!materialOnTheFlyValue.weightValue &&
                    !!materialOnTheFlyValue.weightUnit &&
                    !!materialOnTheFlyValue.weightMeasure;
                if (!isWeightEmpty && !isWeightComplete) {
                    // Weight is only partially filled, so users should not be able to save until they remove or complete it
                    return {
                        disableSaveButton: true,
                        disableMessage: 'Fill out all required fields',
                    };
                }
            } else if (isWeightForbidden) {
                if (
                    !!materialOnTheFlyValue.weightValue ||
                    !!materialOnTheFlyValue.weightUnit ||
                    !!materialOnTheFlyValue.weightMeasure
                ) {
                    // None of the values is allowed, and not every field is empty
                    return {
                        disableSaveButton: true,
                        disableMessage: 'Fill out all required fields',
                    };
                }
            }
        }

        if (isDuplicate) {
            return {
                disableSaveButton: true,
                disableMessage: 'Composition already exists',
            };
        }

        if (!hasChanges) {
            return {
                disableSaveButton: true,
                disableMessage: 'No changes detected',
            };
        }

        if (!isManufactureDetailValid) {
            return {
                disableSaveButton: true,
                disableMessage: 'Invalid MOF',
            };
        }

        return { disableSaveButton: false };
    }, [
        hasChanges,
        includeWeight,
        isCompositionOptional,
        isCompositionRequired,
        isDuplicate,
        isManufactureDetailRequired,
        isManufactureDetailValid,
        isWeightForbidden,
        isWeightOptional,
        isWeightRequired,
        materialOnTheFlyValue,
    ]);

    const isMessageVisible = useMemo(
        () =>
            open &&
            (disableMessage === 'Raw material is not available' ||
                disableMessage === 'Total for composition must be 100%'),
        [open, disableMessage]
    );

    const handleSaveClick = (): void => {
        const hasWeight =
            !!materialOnTheFlyValue.weightValue ||
            !!materialOnTheFlyValue.weightUnit ||
            !!materialOnTheFlyValue.weightMeasure;

        const updatedMaterialOnTheFly = {
            ...materialOnTheFlyValue,
            // Filter out empty compositions (no raw material selected) for optional composition
            compositions: materialOnTheFlyValue.compositions.filter(
                (composition) => !!composition.rawMaterial
            ),
            materialOnTheFlyPublicId:
                materialOnTheFlyValue.materialOnTheFlyPublicId ??
                crypto.randomUUID(), // Use existing ID or generate a new one
            usagePublicId: usagePublicId ?? materialOnTheFlyValue.usagePublicId, // Ensure usagePublicId is set
            manufactureDetail: isManufactureDetailForbidden
                ? null
                : materialOnTheFlyValue.manufactureDetail, // Include manufactureDetail unless it's N/A for the subcategory
            weightValue: !hasWeight ? null : materialOnTheFlyValue.weightValue,
        };
        onClose(updatedMaterialOnTheFly); // we pass the updated material on the fly to the parent component
    };

    const contextValue = useMemo(
        () => ({
            materialOnTheFlyValue,
            dispatchMaterialOnTheFly,
        }),
        [materialOnTheFlyValue]
    );

    // Initialize the material on the fly value when the dialog opens
    useEffect(() => {
        if (open && initialValue) {
            dispatchMaterialOnTheFly({
                type: 'initialize',
                payload: initialValue,
            });
        }
    }, [open, initialValue]);

    useEffect(() => {
        if (!open) {
            // Wait until dialog is faded out to reset the form
            fadeOutDialogTimeout = setTimeout(() => {
                dispatchMaterialOnTheFly({ type: 'clear_mof' });
            }, 500);
        }
        return (): void => {
            clearTimeout(fadeOutDialogTimeout);
        };
    }, [open]);

    // If the materialSubcategories or manufactureDetails change, and there is only one option, preselect it
    useEffect(() => {
        if (materialSubcategories.length === 1) {
            const newMaterialSubcategory = materialSubcategories[0];
            if (
                newMaterialSubcategory.materialSubcategoryPublicId !==
                materialOnTheFlyValue.materialSubcategory
                    ?.materialSubcategoryPublicId
            ) {
                dispatchMaterialOnTheFly({
                    type: 'update_material_subcategory',
                    payload: {
                        newMaterialSubcategory: materialSubcategories[0],
                    },
                });
            }
        }
        if (manufactureDetails.length === 1) {
            const newManufactureDetail = manufactureDetails[0];
            if (
                newManufactureDetail.manufactureDetailPublicId !==
                materialOnTheFlyValue.manufactureDetail
                    ?.manufactureDetailPublicId
            ) {
                dispatchMaterialOnTheFly({
                    type: 'update_manufacture_detail',
                    payload: { newManufactureDetail: manufactureDetails[0] },
                });
            }
        }
    }, [
        manufactureDetails,
        materialOnTheFlyValue.manufactureDetail?.manufactureDetailPublicId,
        materialOnTheFlyValue.materialSubcategory?.materialSubcategoryPublicId,
        materialSubcategories,
    ]);

    // we have to filter the MSC based on the isOnTheFlySupported field. if it is true, we display it in the dropdown
    const filteredMaterialSubcategories = materialSubcategories.filter(
        (subcategory) =>
            'isOnTheFlySupported' in subcategory &&
            subcategory.isOnTheFlySupported
    );

    useEffect(() => {
        latestCompositionsRef.current = materialOnTheFlyValue.compositions;
    }, [materialOnTheFlyValue.compositions]);

    // When composition is optional, start with empty % on initial row
    // (clear only initial/default 100, do not override user input while editing)
    useEffect(() => {
        const compositions = latestCompositionsRef.current;

        if (
            open &&
            isCompositionOptional &&
            compositions.length === 1 &&
            !compositions[0].rawMaterial &&
            compositions[0].percentage === 100
        ) {
            dispatchMaterialOnTheFly({
                type: 'update_composition',
                payload: {
                    composition: {
                        ...compositions[0],
                        percentage: undefined,
                    },
                },
            });
        }
    }, [isCompositionOptional, open]);

    // When composition is required, pre-fill 100% on the initial empty row
    useEffect(() => {
        if (
            isCompositionRequired &&
            materialOnTheFlyValue.compositions.length === 1 &&
            !materialOnTheFlyValue.compositions[0].rawMaterial &&
            materialOnTheFlyValue.compositions[0].percentage === undefined
        ) {
            dispatchMaterialOnTheFly({
                type: 'update_composition',
                payload: {
                    composition: {
                        ...materialOnTheFlyValue.compositions[0],
                        percentage: 100,
                    },
                },
            });
        }
    }, [isCompositionRequired, materialOnTheFlyValue]);

    // When composition is forbidden (N/A), clear existing composition values automatically
    useEffect(() => {
        const hasAnyCompositionValue = materialOnTheFlyValue.compositions.some(
            (composition) => !!composition.rawMaterial
        );

        if (isCompositionForbidden && hasAnyCompositionValue) {
            dispatchMaterialOnTheFly({ type: 'clear_compositions' });
        }
    }, [isCompositionForbidden, materialOnTheFlyValue]);

    // When manufacture detail is forbidden (N/A), clear existing value automatically
    useEffect(() => {
        if (
            isManufactureDetailForbidden &&
            materialOnTheFlyValue.manufactureDetail
        ) {
            dispatchMaterialOnTheFly({
                type: 'update_manufacture_detail',
                payload: { newManufactureDetail: null },
            });
        }
    }, [isManufactureDetailForbidden, materialOnTheFlyValue.manufactureDetail]);

    // Reset manufacture detail validity when it's forbidden (section is hidden, nothing to validate)
    useEffect(() => {
        if (isManufactureDetailForbidden) {
            setIsManufactureDetailValid(true);
        }
    }, [isManufactureDetailForbidden]);

    // When weight is forbidden (N/A), clear existing weight values automatically
    useEffect(() => {
        if (
            isWeightForbidden &&
            (materialOnTheFlyValue.weightValue ||
                materialOnTheFlyValue.weightUnit ||
                materialOnTheFlyValue.weightMeasure)
        ) {
            dispatchMaterialOnTheFly({
                type: 'update_weight_value',
                payload: { newWeightValue: null },
            });
            dispatchMaterialOnTheFly({
                type: 'update_weight_unit',
                payload: { newWeightUnit: null },
            });
            dispatchMaterialOnTheFly({
                type: 'update_weight_measure',
                payload: { newWeightMeasure: null },
            });
        }
    }, [
        isWeightForbidden,
        materialOnTheFlyValue.weightValue,
        materialOnTheFlyValue.weightUnit,
        materialOnTheFlyValue.weightMeasure,
    ]);

    useKeyboardShortcut({
        keyCombo: {
            code: 'KeyV',
            altKey: true,
            description: 'Add/Edit Material',
        },
        callback: () => {
            if (open && !disableSaveButton) {
                handleSaveClick();
            }
        },
        disableRegistry: !open,
    });

    const ManufactureDetailsProviderComponent = isExtendedVersion
        ? ExtendedManufactureDetailsProvider
        : ManufactureDetailsProvider;

    return (
        // Data providers are here to keep the data even though the custom material dialog is closed
        <RawMaterialsProvider
            skip={
                !open ||
                !materialOnTheFlyValue.materialSubcategory
                    ?.materialSubcategoryPublicId
            }
            materialSubcategoryPublicId={
                materialOnTheFlyValue.materialSubcategory
                    ?.materialSubcategoryPublicId
            }
            isExtendedVersion={isExtendedVersion}
            onError={(error, code) => {
                onExtendedError?.(error, code);
            }}
        >
            <ManufactureDetailsProviderComponent
                skip={
                    !open ||
                    !includeManufactureDetail ||
                    !materialOnTheFlyValue.materialSubcategory
                        ?.materialSubcategoryPublicId
                }
                materialSubcategoryPublicId={
                    materialOnTheFlyValue.materialSubcategory
                        ?.materialSubcategoryPublicId
                }
                onError={(error, code) => {
                    onExtendedError?.(error, code);
                }}
            >
                <MaterialOnTheFlyValueContext.Provider value={contextValue}>
                    <Dialog
                        open={open}
                        slotProps={{
                            paper: {
                                sx: {
                                    overflowY: 'visible', // Workaround for expanding dropdown outside of Dialog. This should normally be fixed with disablePortal={false}
                                    minWidth: 768,
                                    backgroundImage: 'none',
                                    backgroundColor: 'background.default',
                                },
                            },
                        }}
                        disableRestoreFocus
                        onClose={(_e, reason): void => {
                            if (reason === 'backdropClick') return;
                            onClose();
                        }}
                    >
                        <MaterialOnTheFlyDialogTitle
                            styleNumber={styleNumber}
                            styleName={styleName}
                            usageName={usageName}
                        />
                        <DialogContent>
                            <Stack spacing={5}>
                                {includeMaterialSubcategory && (
                                    <MaterialSubcategorySection
                                        materialSubcategories={
                                            filteredMaterialSubcategories
                                        }
                                    />
                                )}
                                {/* Show composition section always, disabled until category selected */}
                                {!isCompositionForbidden && (
                                    <RawMaterialsSection
                                        isRequired={isCompositionRequired}
                                    />
                                )}
                                {/* Show weight section always (unless forbidden), disabled until category selected */}
                                {includeWeight && !isWeightForbidden && (
                                    <WeightSection
                                        isRequired={isWeightRequired}
                                    />
                                )}
                                {includeManufactureDetail && (
                                    <ManufactureDetailsSection
                                        isRequired={isManufactureDetailRequired}
                                        isNonApparel={isNonApparel}
                                        onValidityChange={
                                            handleManufactureDetailValidityChange
                                        }
                                    />
                                )}
                                {isMessageVisible && (
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{
                                            alignItems: 'center',
                                            pt: 6,
                                        }}
                                    >
                                        <WarningIcon
                                            color={palette.error.main}
                                        />
                                        <Typography
                                            variant="bodySmall.default"
                                            sx={{
                                                color: palette.error.main,
                                            }}
                                            title={disableMessage}
                                        >
                                            {disableMessage}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="text"
                                fullWidth
                                onClick={(): void => {
                                    onClose();
                                }}
                            >
                                Cancel
                            </Button>
                            <Tooltip
                                title={disableMessage ?? ''}
                                disableHoverListener={!disableSaveButton}
                                disableFocusListener={!disableSaveButton}
                                disableTouchListener={!disableSaveButton}
                            >
                                <Box sx={{ width: 1 }}>
                                    <Button
                                        disabled={disableSaveButton}
                                        fullWidth
                                        onClick={(): void => {
                                            handleSaveClick();
                                        }}
                                    >
                                        {initialValue ? 'Save' : 'Add'}
                                    </Button>
                                </Box>
                            </Tooltip>
                        </DialogActions>
                    </Dialog>
                </MaterialOnTheFlyValueContext.Provider>
            </ManufactureDetailsProviderComponent>
        </RawMaterialsProvider>
    );
};

export { MaterialOnTheFlyDialog };
